"use client";

import { BookOpenText, FileText, Folder, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCreateNotes } from "@/features/ai-notetaker/api/use-create-notes";
import useAuthModal from "@/features/auth/hooks/use-auth-modal";
import useNewFolderModal from "@/features/folders/hooks/use-new-folder-modal";
import useNewStudySetModal from "@/features/studysets/hooks/use-new-studyset-modal";
import { useI18n } from "@/locales/client";

type TabType = "studysets" | "folders" | "notes";

export const EmptyLibraryState = ({
  isAuthenticated,
  activeTab = "studysets",
}: {
  isAuthenticated: boolean;
  activeTab?: string;
}) => {
  const { onOpen } = useNewStudySetModal();
  const { onOpen: onOpenAuth } = useAuthModal();
  const { onOpen: onOpenFolder } = useNewFolderModal();
  const newNoteMutation = useCreateNotes();
  const t = useI18n();

  const tabConfig: Record<
    TabType,
    {
      icon: React.ElementType;
      title: string;
      description: string;
      buttonText: string;
    }
  > = {
    studysets: {
      icon: BookOpenText,
      title: t("emptyLibraryState.studysets.title"),
      description: t("emptyLibraryState.studysets.description"),
      buttonText: t("emptyLibraryState.studysets.buttonText"),
    },
    folders: {
      icon: Folder,
      title: t("emptyLibraryState.folders.title"),
      description: t("emptyLibraryState.folders.description"),
      buttonText: t("emptyLibraryState.folders.buttonText"),
    },
    notes: {
      icon: FileText,
      title: t("emptyLibraryState.notes.title"),
      description: t("emptyLibraryState.notes.description"),
      buttonText: t("emptyLibraryState.notes.buttonText"),
    },
  };

  const currentTab =
    (activeTab as TabType) in tabConfig ? (activeTab as TabType) : "studysets";
  const config = tabConfig[currentTab];
  const Icon = config.icon;

  const handleClick = () => {
    if (!isAuthenticated) {
      onOpenAuth();
      return;
    }
    if (currentTab === "studysets") {
      onOpen();
    } else if (currentTab === "folders") {
      onOpenFolder();
    } else if (currentTab === "notes") {
      newNoteMutation.mutate({
        name: "Untitled Note",
      });
    }
  };

  return (
    <div className="h-[400px] w-full border border-dashed rounded-2xl">
      <div className="flex flex-col items-center justify-center w-full gap-3 h-full">
        <div className="size-12 grid place-items-center rounded border bg-muted/40">
          <Icon className="size-6" />
        </div>
        <div className="flex flex-col justify-center items-center gap-1 text-center">
          <h3 className="font-semibold text-base leading-6">{config.title}</h3>
          <p className="text-sm font-normal leading-5 text-muted-foreground">
            {config.description}
          </p>
        </div>
        <Button
          className="h-8 text-brand-primary hover:text-brand-primary gap-2"
          size="sm"
          onClick={handleClick}
          variant={"ghost"}
        >
          <Plus className="size-4" />
          {config.buttonText}
        </Button>
      </div>
    </div>
  );
};
